import { gql, useQuery } from '@apollo/client';
import { CardBody, CardHeader, CardTitle, Heading, Text } from '@apollo/orbit';
import { Satellite1 } from '@apollo/space-kit/illustrations/Satellite1';
import React from 'react';

import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import {
  DownstreamTaskCard,
  DownstreamTaskCardFragment,
} from '../../checkWorkflowsListPage/checkWorkflowItem/DownstreamTaskCard';
import { sortDownstreamWorkflowResults } from '../../helpers';

export function DownstreamCheckDetails() {
  const { currentDownstreamCheckDetailsRoute } = useInternalGraphLinking();
  const { checkId, graphId } = useRouteParams(
    currentDownstreamCheckDetailsRoute,
  );
  const { data, loading, error } = useQuery<
    GraphQLTypes.DownstreamCheckDetailsQuery,
    GraphQLTypes.DownstreamCheckDetailsQueryVariables
  >(
    gql`
      query DownstreamCheckDetailsQuery($graphId: ID!, $checkId: ID!) {
        service(id: $graphId) {
          id
          checkWorkflow(id: $checkId) {
            id
            downstreamTask {
              id
              ... on DownstreamCheckTask {
                status
                createdAt
                completedAt
                results {
                  ...DownstreamTaskCardFragment
                }
              }
            }
          }
        }
      }
      ${DownstreamTaskCardFragment}
    `,
    {
      variables: { graphId, checkId },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );
  const task = data?.service?.checkWorkflow?.downstreamTask;

  if (loading) {
    return <Loading />;
  } else if (error) {
    return (
      <div className="p-2">
        <ErrorMessage error={error} />
      </div>
    );
  } else if (task?.__typename !== 'DownstreamCheckTask') {
    return <NotFound />;
  }

  const results = task.results ?? [];
  const downstreamWorkflows = sortDownstreamWorkflowResults(results);

  if (!isMinLengthArray(1, downstreamWorkflows)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center space-y-3 px-6">
        <Satellite1 className="w-[180px]" />
        <Heading as="h1" size="xl">
          No downstream workflows
        </Heading>
        <Text variant="subtitle" className="max-w-2xl text-center">
          Honestly, not sure how you got here!
        </Text>
      </div>
    );
  }

  return (
    <>
      <CardHeader>
        <CardTitle>
          Downstream workflows{' '}
          <span className="text-secondary">({downstreamWorkflows.length})</span>
        </CardTitle>
      </CardHeader>
      <CardBody className="overflow-y-auto p-0 last:pb-0">
        {downstreamWorkflows.map(
          ({ downstreamWorkflow, failsUpstreamWorkflow }) => (
            <div
              key={downstreamWorkflow?.id}
              className="border-b border-primary py-6 last:border-b-0"
            >
              <DownstreamTaskCard
                downstreamWorkflow={downstreamWorkflow}
                failsUpstreamWorkflow={failsUpstreamWorkflow}
              />
            </div>
          ),
        )}
      </CardBody>
    </>
  );
}
